import { useCallback } from 'react';
import type { FieldValues, UseFormSetError } from 'react-hook-form';

import { applyFieldErrors, useErrorMessage } from '@nucleus-commerce/admin-kit/errors';
import { isApiError } from '@nucleus-commerce/api-client/transport';

export interface StageFormErrorOptions {
  /** Form paths the dialog renders; other field errors go to the dialog's alert. */
  knownFields: ReadonlySet<string>;
  mapField?: (apiField: string) => string;
  /** Called on 422 DELAY_REASON_REQUIRED so the dialog shows the reason field it had hidden. */
  onDelayReasonRequired?: () => void;
}

/**
 * Routes a refused stage action into the dialog's form: backend field errors to their inputs
 * (paths like `body.parcels[0].key` are dotted first), anything else to the form-level alert.
 */
export function useStageFormError<T extends FieldValues>(
  setError: UseFormSetError<T>,
  { knownFields, mapField, onDelayReasonRequired }: StageFormErrorOptions,
) {
  const message = useErrorMessage();
  return useCallback(
    (error: unknown, mapThisField?: (field: string) => string) => {
      if (isApiError(error) && error.code === 'DELAY_REASON_REQUIRED') onDelayReasonRequired?.();
      const map = mapThisField ?? mapField;
      const applied = applyFieldErrors(error, setError, {
        knownFields,
        mapField: (field) => {
          const dotted = field.replace(/\[(\d+)\]/g, '.$1');
          return map ? map(dotted) : dotted;
        },
      });
      if (!applied) setError('root.server', { type: 'server', message: message(error) });
    },
    [setError, knownFields, mapField, message, onDelayReasonRequired],
  );
}
